/**
 * Rueckkanal Zentrale → Quelle (Spec 5.3) und das Zustellprotokoll dazu.
 *
 * Drei Ereignisse, mehr sieht die Spec nicht vor:
 *
 * | Event | Wann | Wer loest aus |
 * |---|---|---|
 * | `ticket.reply_created` | Agent antwortet, Quelle hat `webhook_url` | `services/replies.ts` |
 * | `ticket.status_changed` | jeder Statuswechsel am Ticket | `services/tickets.ts` (`applyStatus`) |
 * | `ticket.merged` | Ticket in ein anderes verschmolzen | **Phase 5** — Schema und Emitter stehen |
 *
 * Der Umschlag ist fuer alle drei gleich; `data` haengt am `event`
 * (diskriminierte Union). `id` ist **stabil ueber alle Zustellversuche** — die
 * Quell-App kann damit doppelte Zustellungen erkennen und verwerfen. Das ist
 * kein theoretischer Fall: nach einem Timeout wiederholen wir, obwohl der
 * Empfaenger die Nachricht vielleicht schon verarbeitet hat (at-least-once).
 *
 * Signiert wird der rohe JSON-Body mit `signWebhook()` aus
 * `../webhook-signature.js` — Format und Replayschutz stehen dort.
 *
 * Doku: docs/services/webhooks.md
 */
import { z } from 'zod';
import { paginationQuerySchema } from '../envelope.js';
import { uuidSchema } from './common.js';
import { publicTicketMessageSchema } from './ingest.js';
import { ticketNumberSchema, ticketPrioritySchema, ticketStatusSchema } from './ticket.js';
export const WEBHOOK_EVENTS = [
    'ticket.reply_created',
    'ticket.status_changed',
    'ticket.merged',
];
export const webhookEventSchema = z.enum(WEBHOOK_EVENTS);
/**
 * Ticket-Kurzform in jedem Ereignis. Bewusst **nicht** das ganze Ticket: die
 * App haelt ihre eigene Anzeige (Invariante 4) und braucht nur genug, um die
 * Zuordnung herzustellen — `external_ref` ist ihr eigener Schluessel.
 */
export const webhookTicketRefSchema = z.object({
    id: uuidSchema,
    number: ticketNumberSchema,
    external_ref: z.string().nullable(),
    subject: z.string(),
    status: ticketStatusSchema,
    priority: ticketPrioritySchema,
});
export const ticketReplyCreatedDataSchema = z.object({
    ticket: webhookTicketRefSchema,
    message: publicTicketMessageSchema,
});
export const ticketStatusChangedDataSchema = z.object({
    ticket: webhookTicketRefSchema,
    from: ticketStatusSchema,
    to: ticketStatusSchema,
    /** `true`, wenn der Wechsel aus der Automatik nach Spec 3.3 kam. */
    automatic: z.boolean(),
});
export const ticketMergedDataSchema = z.object({
    ticket: webhookTicketRefSchema,
    /** Das Ticket, in das verschmolzen wurde — dort geht es weiter. */
    merged_into: webhookTicketRefSchema,
});
const envelopeBase = {
    /** Stabil ueber alle Zustellversuche — Dedup-Schluessel fuer den Empfaenger. */
    id: uuidSchema,
    occurred_at: z.iso.datetime(),
    tenant_id: uuidSchema,
    source_id: uuidSchema,
};
export const webhookPayloadSchema = z.discriminatedUnion('event', [
    z.object({
        ...envelopeBase,
        event: z.literal('ticket.reply_created'),
        data: ticketReplyCreatedDataSchema,
    }),
    z.object({
        ...envelopeBase,
        event: z.literal('ticket.status_changed'),
        data: ticketStatusChangedDataSchema,
    }),
    z.object({
        ...envelopeBase,
        event: z.literal('ticket.merged'),
        data: ticketMergedDataSchema,
    }),
]);
// ───────────────────────────────────────────────── Zustellprotokoll
/**
 * `pending` → `delivered` (2xx) bzw. `failed` (alle Versuche verbraucht oder
 * dauerhaft abgelehnt). Eine Zeile je Ereignis, nicht je Versuch — `attempt`
 * zaehlt hoch. Gleiche Entscheidung wie bei den Mails (ADR 0002).
 */
export const WEBHOOK_DELIVERY_STATUSES = ['pending', 'delivered', 'failed'];
export const webhookDeliveryStatusSchema = z.enum(WEBHOOK_DELIVERY_STATUSES);
export const webhookDeliverySchema = z.object({
    id: uuidSchema,
    tenant_id: uuidSchema,
    source_id: uuidSchema,
    ticket_id: uuidSchema.nullable(),
    event: webhookEventSchema,
    /** `id` aus dem Umschlag — steht so beim Empfaenger im Log. */
    event_id: uuidSchema,
    status: webhookDeliveryStatusSchema,
    attempt: z.number().int().min(0),
    /** HTTP-Status der letzten Antwort; `null` bei Netzwerkfehlern. */
    response_code: z.number().int().nullable(),
    /** Kurzbegruendung des letzten Fehlschlags. */
    error: z.string().nullable(),
    next_retry_at: z.iso.datetime().nullable(),
    delivered_at: z.iso.datetime().nullable(),
    payload: z.record(z.string(), z.unknown()),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
export const webhookDeliveryQuerySchema = paginationQuerySchema.extend({
    source_id: uuidSchema.optional(),
    status: webhookDeliveryStatusSchema.optional(),
    event: webhookEventSchema.optional(),
});
//# sourceMappingURL=webhook.js.map